import {
    type PointerEvent as ReactPointerEvent,
    type ReactNode,
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";
import "./styles.scss";

interface ResizableSplitViewProps {
    left: ReactNode;
    right: ReactNode;
    direction?: "horizontal" | "vertical";
    defaultLeftSize?: number;
    minLeftSize?: number;
    minRightSize?: number;
    dividerSize?: number;
    showHandle?: boolean;
    className?: string;
    storageKey?: string;
}

function clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
}

export default function ResizableSplitView({
                                               left,
                                               right,
                                               direction = "horizontal",
                                               defaultLeftSize = 280,
                                               minLeftSize = 180,
                                               minRightSize = 360,
                                               dividerSize = 8,
                                               showHandle = true,
                                               className = "",
                                               storageKey,
                                           }: ResizableSplitViewProps) {
    const isVertical = direction === "vertical";
    const containerRef = useRef<HTMLDivElement | null>(null);
    const dragStateRef = useRef<{ pointerId: number | null; startPos: number; startFirstSize: number }>({
        pointerId: null,
        startPos: 0,
        startFirstSize: defaultLeftSize,
    });
    const [leftSize, setLeftSize] = useState(defaultLeftSize);

    const getMaxLeftSize = useCallback(() => {
        const containerExtent = isVertical
            ? (containerRef.current?.clientHeight ?? 0)
            : (containerRef.current?.clientWidth ?? 0);
        return Math.max(minLeftSize, containerExtent - minRightSize - dividerSize);
    }, [dividerSize, isVertical, minLeftSize, minRightSize]);

    const clampLeftSize = useCallback((nextLeftSize: number) => {
        return clamp(nextLeftSize, minLeftSize, getMaxLeftSize());
    }, [getMaxLeftSize, minLeftSize]);

    const stopDragging = useCallback(() => {
        dragStateRef.current.pointerId = null;
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
    }, []);

    const handlePointerMove = useCallback((event: PointerEvent) => {
        if (dragStateRef.current.pointerId !== event.pointerId) return;

        const currentPos = isVertical ? event.clientY : event.clientX;
        const delta = currentPos - dragStateRef.current.startPos;
        const nextLeftSize = dragStateRef.current.startFirstSize + delta;
        setLeftSize(clampLeftSize(nextLeftSize));
    }, [clampLeftSize, isVertical]);

    const handlePointerUp = useCallback((event: PointerEvent) => {
        if (dragStateRef.current.pointerId !== event.pointerId) return;
        stopDragging();
    }, [stopDragging]);

    const handleDividerPointerDown = useCallback((event: ReactPointerEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();

        dragStateRef.current.pointerId = event.pointerId;
        dragStateRef.current.startPos = isVertical ? event.clientY : event.clientX;
        dragStateRef.current.startFirstSize = leftSize;

        document.body.style.cursor = isVertical ? "ns-resize" : "ew-resize";
        document.body.style.userSelect = "none";
    }, [isVertical, leftSize]);

    useEffect(() => {
        const handleWindowResize = () => {
            setLeftSize((current) => clampLeftSize(current));
        };

        window.addEventListener("resize", handleWindowResize);
        return () => {
            window.removeEventListener("resize", handleWindowResize);
        };
    }, [clampLeftSize]);

    useEffect(() => {
        window.addEventListener("pointermove", handlePointerMove);
        window.addEventListener("pointerup", handlePointerUp);

        return () => {
            window.removeEventListener("pointermove", handlePointerMove);
            window.removeEventListener("pointerup", handlePointerUp);
            stopDragging();
        };
    }, [handlePointerMove, handlePointerUp, stopDragging]);

    useEffect(() => {
        setLeftSize((current) => clampLeftSize(current));
    }, [clampLeftSize]);

    useEffect(() => {
        if (!storageKey) return;

        try {
            const raw = localStorage.getItem(storageKey);
            if (!raw) return;

            const parsed = Number(raw);
            if (!Number.isFinite(parsed)) return;
            setLeftSize(clampLeftSize(parsed));
        } catch {
            // Ignore storage errors (private mode / denied access).
        }
    }, [clampLeftSize, storageKey]);

    useEffect(() => {
        if (!storageKey) return;

        try {
            localStorage.setItem(storageKey, String(leftSize));
        } catch {
            // Ignore storage errors (private mode / denied access).
        }
    }, [leftSize, storageKey]);

    const containerClass = [
        "resizable-split-view",
        isVertical && "resizable-split-view--vertical",
        className,
    ].filter(Boolean).join(" ");

    const firstPanelStyle = isVertical
        ? { height: `${leftSize}px` }
        : { width: `${leftSize}px` };

    const dividerStyle = isVertical
        ? { height: `${dividerSize}px` }
        : { width: `${dividerSize}px` };

    return (
        <div ref={containerRef} className={containerClass}>
            <div className="resizable-split-view__left" style={firstPanelStyle}>
                {left}
            </div>
            <button
                type="button"
                className="resizable-split-view__divider"
                style={dividerStyle}
                aria-label="Resize panels"
                onPointerDown={handleDividerPointerDown}
            >
                {showHandle && (
                    <span className={`resizable-split-view__handle ${isVertical ? "resizable-split-view__handle--vertical" : ""}`} />
                )}
            </button>
            <div className="resizable-split-view__right">
                {right}
            </div>
        </div>
    );
}
